import React from 'react'

import { Dropdown, useModalContext } from '@sketch/components'
import {
  ProjectInSidebarAndHeaderFragment,
  ProjectListItemFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { useFlag } from '@sketch/modules-common'

import {
  CopyProjectAction,
  CreateCollectionAction,
  DeleteProjectAction,
  LeaveProjectAction,
  RenameProjectAction,
  ShareProjectAction,
  PinProjectAction,
} from '../ProjectDropdownActions'

import ArchiveProjectModal from '../../modals/ArchiveProjectModal'
import UnarchiveProjectModal from '../../modals/UnarchiveProjectModal'

interface ProjectDropdownProps {
  workspace: WorkspaceMinimalFragment
  project: ProjectInSidebarAndHeaderFragment | ProjectListItemFragment
  onRename: () => void
}

const ProjectDropdown = (props: ProjectDropdownProps) => {
  const { workspace, project, onRename } = props
  const workspaceId = props.workspace.identifier

  const { showModal } = useModalContext()

  // Remove this FF when released
  // https://github.com/sketch-hq/Cloud/issues/18702
  const hasGuestsInProjects = useFlag('guests-in-project')

  const pinned = project.pinnedByCurrentUserAt
  const { userCanEdit, userCanAdminister, features } = workspace
  const isArchived = !!project.archivedAt

  // only editors and admins can perform the following actions:
  //   - Add/remove people from the project (<ShareProjectAction .../>)
  //   - Rename the project (<RenameProjectAction .../>)
  //   - Delete the project (<DeleteProjectAction .../>)
  const showEditActions = userCanEdit || userCanAdminister
  // The `Leave...` button is being added as part of the guests-in-projects feature,
  // only users with the feature flag enabled AND who can leave the project
  // should see the button.
  const showLeaveAction = hasGuestsInProjects && project.userCanLeave
  const showDivider = showEditActions || showLeaveAction
  const showArchive = showEditActions && features.archiveEnabled

  if (project.archivedAt) {
    return (
      <>
        <Dropdown.Header>Project</Dropdown.Header>
        <CopyProjectAction project={project} workspaceId={workspaceId} />
        {showEditActions && <RenameProjectAction onRename={onRename} />}
        {showEditActions && (
          <Dropdown.Item
            onClick={() => {
              showModal(UnarchiveProjectModal, {
                projectIdentifier: project.identifier,
                projectName: project.name,
                workspaceIdentifier: workspaceId,
              })
            }}
          >
            Unarchive&hellip;
          </Dropdown.Item>
        )}
        {showDivider && <Dropdown.Divider />}
        {showLeaveAction && (
          <LeaveProjectAction
            projectIdentifier={project.identifier}
            projectName={project.name}
          />
        )}
        {showEditActions && (
          <DeleteProjectAction project={project} workspaceId={workspaceId} />
        )}
      </>
    )
  }

  return (
    <>
      <Dropdown.Header>Project</Dropdown.Header>
      {showEditActions && (
        <ShareProjectAction project={project} workspace={workspace} />
      )}
      {showEditActions && <RenameProjectAction onRename={onRename} />}
      {showEditActions && (
        <CreateCollectionAction
          project={project}
          workspaceIdentifier={workspaceId}
        />
      )}
      <CopyProjectAction project={project} workspaceId={workspaceId} />
      {!isArchived && (
        <PinProjectAction
          projectIdentifier={project.identifier}
          pinned={pinned}
          workspaceId={workspaceId}
        />
      )}
      {showArchive && (
        <Dropdown.Item
          onClick={() => {
            showModal(ArchiveProjectModal, {
              project,
              workspaceIdentifier: workspaceId,
            })
          }}
        >
          Archive&hellip;
        </Dropdown.Item>
      )}
      {showDivider && <Dropdown.Divider />}
      {showLeaveAction && (
        <LeaveProjectAction
          projectIdentifier={project.identifier}
          projectName={project.name}
        />
      )}
      {showEditActions && (
        <DeleteProjectAction project={project} workspaceId={workspaceId} />
      )}
    </>
  )
}

export default ProjectDropdown
